import * as React from 'react';
import {
  ChevronDown,
  ChevronUp,
  Crown,
  Loader2,
  Pencil,
  Plus,
  PowerOff,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import {
  formatPlanPrice,
  tierLabel,
  useDeleteMembershipPlan,
  useMembershipPlans,
  useRenameMembershipPlan,
  useReorderMembershipPlans,
  useUpsertMembershipPlan,
  type MembershipPlanInput,
  type MembershipPlanRow,
} from '../../hooks/useMembershipPlans';
import { useSeedAnfaengerPlans } from '../../hooks/useBilling';
import { HeaderActions } from '../../components/HeaderActions';
import { cn } from '../../lib/utils';

const TIERS = ['trial', 'free', 'pro', 'enterprise', 'ultimate'] as const;
const CANONICAL_PLAN_IDS = [
  'pro_subscription',
  'payg_subscription',
  'enterprise_subscription',
  'ultimate_subscription',
] as const;
// Locked-IDs set covers BOTH the new canonical IDs AND the legacy
// Anfänger/Advanced ones — until an admin runs the seed-migration,
// the old docs are still around and renaming them by hand would
// silently break Mollie. After re-seed they're gone.
const LOCKED_PLAN_IDS: Set<string> = new Set([
  ...CANONICAL_PLAN_IDS,
  'anfaenger_subscription',
  'anfaenger_payg',
  'advanced_subscription',
]);
// Backwards-compat alias so existing references continue to compile.
const ANFAENGER_IDS = LOCKED_PLAN_IDS;

export function MembershipPlansPage() {
  const plans = useMembershipPlans();
  const seed = useSeedAnfaengerPlans();
  const reorder = useReorderMembershipPlans();
  const { toast } = useToast();
  const [editing, setEditing] = React.useState<MembershipPlanRow | 'new' | null>(null);
  const [confirmReseed, setConfirmReseed] = React.useState(false);

  // Catalogue is "complete" only when all four canonical plans exist
  // (Pro / PAYG / Enterprise / Ultimate). Missing any one of them
  // surfaces the seed-CTA banner.
  const presentIds = new Set((plans.data ?? []).map((p) => p.id));
  const missing = CANONICAL_PLAN_IDS.filter((id) => !presentIds.has(id));
  const needsSeed = !plans.isLoading && missing.length > 0;

  async function onSeed() {
    try {
      await seed.mutateAsync();
      toast({
        title: 'Standard-Pläne aktualisiert',
        description:
          'Pro, Pay-as-you-go, Enterprise und Ultimate sind im Katalog. Andere Pläne wurden entfernt.',
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Button size="sm" variant="outline" onClick={() => setConfirmReseed(true)}>
          <Sparkles className="mr-2 h-4 w-4" /> Standard-Pläne neu setzen
        </Button>
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus className="mr-2 h-4 w-4" /> Neuer Plan
        </Button>
      </HeaderActions>

      {needsSeed && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle>Standard-Pläne fehlen</CardTitle>
            <CardDescription>
              Der Katalog kennt vier kanonische Pläne:{' '}
              <strong>Pro · Monats-Abo</strong>, <strong>Pay-as-you-go</strong>,{' '}
              <strong>Enterprise (49 €/Monat)</strong> und{' '}
              <strong>Ultimate (99 €/Monat)</strong>. Aktuell fehlen:{' '}
              <strong>{missing.join(', ')}</strong>. Mollie-Checkout, Quotenprüfung
              und Rechnungsgenerator lesen aus diesen Dokumenten — Klick legt sie
              mit sinnvollen Defaults an; Werte änderst du danach jederzeit hier.
              Ultimate startet privat (für niemanden sichtbar) und muss pro UID
              freigeschaltet werden.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => void onSeed()} disabled={seed.isPending}>
              {seed.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Standard-Pläne anlegen
            </Button>
          </CardContent>
        </Card>
      )}

      {confirmReseed && (
        <Dialog open onOpenChange={(o) => !o && setConfirmReseed(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Standard-Pläne neu setzen?</DialogTitle>
              <DialogDescription className="pt-2">
                Setzt die vier kanonischen Pläne (Pro · Monats-Abo, Pay-as-you-go,
                Enterprise, Ultimate) auf ihre Werks-Defaults — Name, Beschreibung,
                Features und Preisfelder werden zurückgeschrieben.{' '}
                <strong>Alle Pläne mit anderer ID werden gelöscht.</strong> Bestehende
                Mitgliedschaften, die auf gelöschte Plan-IDs verweisen, behalten ihren
                Tier — die Verknüpfung läuft aber ins Leere.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmReseed(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={async () => {
                  setConfirmReseed(false);
                  await onSeed();
                }}
                disabled={seed.isPending}
              >
                {seed.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Ja, neu setzen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Alle Pläne</CardTitle>
          <CardDescription>
            {plans.isLoading
              ? 'Lade …'
              : `${plans.data?.filter((p) => p.isActive).length ?? 0} aktiv von ${plans.data?.length ?? 0}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {plans.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !plans.data || plans.data.length === 0 ? (
            <EmptyState onCreate={() => setEditing('new')} />
          ) : (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Reihenfolge</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Preis</TableHead>
                      <TableHead>Dauer</TableHead>
                      <TableHead>Features</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plans.data.map((p, idx) => (
                      <PlanRow
                        key={p.id}
                        plan={p}
                        onEdit={() => setEditing(p)}
                        onMoveUp={
                          idx === 0
                            ? null
                            : () =>
                                void reorderRow(plans.data!, idx, idx - 1, reorder, toast)
                        }
                        onMoveDown={
                          idx === (plans.data?.length ?? 0) - 1
                            ? null
                            : () =>
                                void reorderRow(plans.data!, idx, idx + 1, reorder, toast)
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3">
                {plans.data.map((p) => (
                  <PlanCard key={p.id} plan={p} onEdit={() => setEditing(p)} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditPlanDialog
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * Builds a sortOrder swap between two adjacent plans and posts it via
 * the reorder callable. We re-number both rows (10/20/30/...) so the
 * back-end batch-write is minimal and a future drag-drop refactor can
 * reuse the same gap-based numbering without colliding.
 */
async function reorderRow(
  rows: MembershipPlanRow[],
  fromIdx: number,
  toIdx: number,
  reorder: ReturnType<typeof useReorderMembershipPlans>,
  toast: ReturnType<typeof useToast>['toast'],
): Promise<void> {
  if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
  if (fromIdx >= rows.length || toIdx >= rows.length) return;
  const next = [...rows];
  const [moved] = next.splice(fromIdx, 1);
  if (!moved) return;
  next.splice(toIdx, 0, moved);
  // Re-number with 10-step gaps so future inserts can slot between.
  const entries = next.map((p, i) => ({ id: p.id, sortOrder: (i + 1) * 10 }));
  try {
    await reorder.mutateAsync(entries);
  } catch (err) {
    toast({
      title: 'Sortieren fehlgeschlagen',
      description: (err as Error).message,
      variant: 'destructive',
    });
  }
}

function PlanRow({
  plan,
  onEdit,
  onMoveUp,
  onMoveDown,
}: {
  plan: MembershipPlanRow;
  onEdit: () => void;
  onMoveUp: (() => void) | null;
  onMoveDown: (() => void) | null;
}) {
  const remove = useDeleteMembershipPlan();
  const { toast } = useToast();

  async function deactivate() {
    if (!plan.isActive) return;
    if (!confirm(`Plan „${plan.name}" wirklich deaktivieren?`)) return;
    try {
      await remove.mutateAsync(plan.id);
      toast({ title: 'Plan deaktiviert' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <TableRow className={cn(!plan.isActive && 'opacity-60')}>
      <TableCell>
        <div className="flex flex-col items-center gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onMoveUp?.()}
            disabled={!onMoveUp}
            aria-label="Höher sortieren"
            title="Nach oben"
          >
            <ChevronUp className="size-3" />
          </Button>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {plan.sortOrder ?? 0}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onMoveDown?.()}
            disabled={!onMoveDown}
            aria-label="Tiefer sortieren"
            title="Nach unten"
          >
            <ChevronDown className="size-3" />
          </Button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">{plan.name}</span>
          <span className="font-mono text-xs text-muted-foreground">{plan.id}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="default">{tierLabel(plan.tier)}</Badge>
          {plan.saasKind === 'subscription' && (
            <Badge variant="outline">Abo</Badge>
          )}
          {plan.saasKind === 'payg' && (
            <Badge variant="outline">PAYG</Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium">{formatPlanPrice(plan)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {plan.durationDays ? `${plan.durationDays} Tage` : 'Unbegrenzt'}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {plan.features.slice(0, 3).map((f, i) => (
            <Badge key={i} variant="muted">
              {f.length > 24 ? `${f.slice(0, 24)}…` : f}
            </Badge>
          ))}
          {plan.features.length > 3 && (
            <Badge variant="outline">+{plan.features.length - 3}</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        {plan.isActive ? (
          <Badge variant="success">Aktiv</Badge>
        ) : (
          <Badge variant="muted">Inaktiv</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} title="Bearbeiten">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={deactivate}
            disabled={!plan.isActive || remove.isPending}
            title="Deaktivieren"
          >
            <PowerOff className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function PlanCard({ plan, onEdit }: { plan: MembershipPlanRow; onEdit: () => void }) {
  return (
    <Card className={cn(!plan.isActive && 'opacity-60')}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" />
              <span className="truncate font-semibold">{plan.name}</span>
            </div>
            <div className="font-mono text-xs text-muted-foreground">{plan.id}</div>
          </div>
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="default">{tierLabel(plan.tier)}</Badge>
          <Badge variant={plan.isActive ? 'success' : 'muted'}>
            {plan.isActive ? 'Aktiv' : 'Inaktiv'}
          </Badge>
          <Badge variant="outline">
            {plan.durationDays ? `${plan.durationDays} Tage` : 'Unbegrenzt'}
          </Badge>
        </div>
        <div className="text-2xl font-semibold">{formatPlanPrice(plan)}</div>
        {plan.features.length > 0 && (
          <ul className="space-y-1 text-xs text-muted-foreground">
            {plan.features.slice(0, 4).map((f, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
            {plan.features.length > 4 && (
              <li className="text-[11px] italic">… und {plan.features.length - 4} weitere</li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border p-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
        <Crown className="h-5 w-5 text-primary" />
      </div>
      <div className="text-sm font-medium">Noch keine Pläne angelegt</div>
      <p className="max-w-sm text-xs text-muted-foreground">
        Lege Pläne mit Preis, Dauer und Features an, um sie deinen Benutzern zuzuweisen.
      </p>
      <Button onClick={onCreate}>
        <Plus className="h-4 w-4" /> Ersten Plan anlegen
      </Button>
    </div>
  );
}

function EditPlanDialog({
  plan,
  onClose,
}: {
  plan: MembershipPlanRow | null;
  onClose: () => void;
}) {
  const upsert = useUpsertMembershipPlan();
  const rename = useRenameMembershipPlan();
  const { toast } = useToast();
  const isEdit = plan != null;
  const originalId = plan?.id ?? '';

  const [id, setId] = React.useState(plan?.id ?? '');
  const [name, setName] = React.useState(plan?.name ?? '');
  const [tier, setTier] = React.useState<MembershipPlanRow['tier']>(plan?.tier ?? 'pro');
  const [description, setDescription] = React.useState(plan?.description ?? '');
  const [priceEuro, setPriceEuro] = React.useState(
    plan ? (plan.priceMonthlyCents / 100).toString() : '0',
  );
  const [currency, setCurrency] = React.useState<'EUR' | 'USD'>(plan?.currency ?? 'EUR');
  const [durationDays, setDurationDays] = React.useState(
    plan?.durationDays != null ? String(plan.durationDays) : '',
  );
  const [unlimited, setUnlimited] = React.useState(plan?.durationDays == null);
  const [callMinutes, setCallMinutes] = React.useState(
    plan?.callMinutesIncluded != null ? String(plan.callMinutesIncluded) : '',
  );
  const [features, setFeatures] = React.useState<string[]>(plan?.features ?? []);
  const [featureDraft, setFeatureDraft] = React.useState('');
  const [isActive, setIsActive] = React.useState(plan?.isActive ?? true);
  const [sortOrder, setSortOrder] = React.useState(String(plan?.sortOrder ?? 100));

  // SaaS-Anfänger-Felder — angezeigt sobald `saasKind !== null`. Die zwei
  // Anfänger-Plan-IDs setzen `saasKind` automatisch.
  const initialSaasKind = plan?.saasKind ?? null;
  const [saasKind, setSaasKind] = React.useState<'subscription' | 'payg' | null>(
    initialSaasKind ?? null,
  );
  const [overagePerMinute, setOveragePerMinute] = React.useState(
    plan?.overagePerMinuteNetCents != null ? String(plan.overagePerMinuteNetCents) : '15',
  );
  const [perMinute, setPerMinute] = React.useState(
    plan?.perMinuteNetCents != null ? String(plan.perMinuteNetCents) : '15',
  );
  const [defaultTopUp, setDefaultTopUp] = React.useState(
    plan?.defaultTopUpNetCents != null ? String(plan.defaultTopUpNetCents) : '2500',
  );
  const [minTopUp, setMinTopUp] = React.useState(
    plan?.minTopUpNetCents != null ? String(plan.minTopUpNetCents) : '500',
  );

  // Visibility — controls who sees this plan in the user-facing
  // PlanSwitchCard. `'public'` = visible to everyone. `'private'` =
  // only visible to UIDs in the allowedUids list. Older docs without
  // a `visibility` field default to public for backwards compat.
  const [visibilityMode, setVisibilityMode] = React.useState<'public' | 'private'>(
    plan?.visibility?.mode ?? 'public',
  );
  const [allowedUidsText, setAllowedUidsText] = React.useState(
    (plan?.visibility?.allowedUids ?? []).join('\n'),
  );

  // Canonical IDs each have a saasKind contract that the Mollie webhook
  // + loadPricingPlans() rely on. We force the right saasKind on those
  // four IDs so the admin can't accidentally flip e.g. Pro Subscription
  // into a PAYG plan.
  const lockedSubscription =
    id === 'pro_subscription'
    || id === 'enterprise_subscription'
    || id === 'ultimate_subscription'
    || id === 'anfaenger_subscription'
    || id === 'advanced_subscription';
  const lockedPayg = id === 'payg_subscription' || id === 'anfaenger_payg';
  const saasKindLocked = lockedSubscription || lockedPayg;
  React.useEffect(() => {
    if (lockedSubscription && saasKind !== 'subscription') {
      setSaasKind('subscription');
    } else if (lockedPayg && saasKind !== 'payg') {
      setSaasKind('payg');
    }
  }, [lockedSubscription, lockedPayg, saasKind]);

  function addFeature() {
    const trimmed = featureDraft.trim();
    if (!trimmed) return;
    if (features.length >= 30) return;
    setFeatures((f) => [...f, trimmed]);
    setFeatureDraft('');
  }

  function removeFeature(idx: number) {
    setFeatures((f) => f.filter((_, i) => i !== idx));
  }

  async function onSave() {
    const priceCents = Math.round(Math.max(0, parseFloat(priceEuro || '0')) * 100);
    const payload: MembershipPlanInput = {
      id: id.trim() || autoSlug(name),
      name: name.trim(),
      tier,
      description: description.trim(),
      priceMonthlyCents: priceCents,
      currency,
      durationDays: unlimited ? null : parseInt(durationDays || '0', 10) || null,
      features,
      callMinutesIncluded: callMinutes.trim() === '' ? null : parseInt(callMinutes, 10),
      isActive,
      sortOrder: parseInt(sortOrder || '100', 10) || 100,
      saasKind: saasKind ?? null,
      overagePerMinuteNetCents:
        saasKind === 'subscription' ? parseInt(overagePerMinute || '0', 10) || 0 : null,
      perMinuteNetCents:
        saasKind === 'payg' ? parseInt(perMinute || '0', 10) || 0 : null,
      defaultTopUpNetCents:
        saasKind === 'payg' ? parseInt(defaultTopUp || '0', 10) || 0 : null,
      minTopUpNetCents:
        saasKind === 'payg' ? parseInt(minTopUp || '0', 10) || 0 : null,
      visibility: {
        mode: visibilityMode,
        // Split textarea on newlines/commas, trim, drop empties.
        // No length-validation here — schema caps to 500 entries.
        allowedUids: allowedUidsText
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      },
    };
    if (!payload.name) {
      toast({ title: 'Name fehlt', variant: 'destructive' });
      return;
    }
    if (!payload.id) {
      toast({ title: 'ID fehlt', variant: 'destructive' });
      return;
    }
    try {
      // Detect ID change on edit → atomic rename first, then upsert
      // updates the renamed doc with the rest of the form data.
      const idChanged = isEdit && originalId && originalId !== payload.id;
      if (idChanged) {
        await rename.mutateAsync({ fromId: originalId, toId: payload.id });
      }
      await upsert.mutateAsync(payload);
      toast({
        title: isEdit
          ? idChanged
            ? `Plan umbenannt: ${originalId} → ${payload.id}`
            : 'Plan aktualisiert'
          : 'Plan angelegt',
      });
      onClose();
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Plan bearbeiten' : 'Neuer Plan'}</DialogTitle>
          <DialogDescription>
            Lege Preis, Dauer und Features fest. Der Tier dient als Kategorie für bestehende Logik.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="plan-name">Name</Label>
              <Input
                id="plan-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!isEdit && !id) setId(autoSlug(e.target.value));
                }}
                placeholder="z.B. Starter Pro Monatlich"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-id">ID</Label>
              <Input
                id="plan-id"
                value={id}
                // Canonical seed-IDs are wired into the Mollie webhook +
                // pricing loader on the backend. Renaming them would
                // silently break billing, so we lock the input in that
                // case. All other plans are free to rename — the upsert
                // handler detects the change and dispatches the rename
                // callable on save.
                disabled={isEdit && ANFAENGER_IDS.has(id)}
                onChange={(e) => setId(e.target.value)}
                placeholder="starter-pro"
              />
              {isEdit && ANFAENGER_IDS.has(id) && (
                <p className="text-[11px] text-muted-foreground">
                  Standard-Plan-ID gesperrt — wird vom Backend (Mollie + Quotenprüfung) verdrahtet referenziert.
                </p>
              )}
              {isEdit && !ANFAENGER_IDS.has(id) && (
                <p className="text-[11px] text-muted-foreground">
                  Beim Umbenennen werden bestehende User-Mitgliedschaften automatisch nachgezogen.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tier-Kategorie</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as MembershipPlanRow['tier'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {tierLabel(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-[1fr_120px] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="plan-price">Preis pro Monat</Label>
                <Input
                  id="plan-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceEuro}
                  onChange={(e) => setPriceEuro(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Währung</Label>
                <Select value={currency} onValueChange={(v) => setCurrency(v as 'EUR' | 'USD')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="plan-duration">Laufzeit (Tage)</Label>
              <Input
                id="plan-duration"
                type="number"
                min="1"
                value={durationDays}
                onChange={(e) => {
                  setDurationDays(e.target.value);
                  if (e.target.value) setUnlimited(false);
                }}
                disabled={unlimited}
                placeholder="30"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={unlimited}
                  onChange={(e) => setUnlimited(e.target.checked)}
                />
                Unbegrenzt
              </label>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-minutes">Inkl. Telefonminuten (optional)</Label>
              <Input
                id="plan-minutes"
                type="number"
                min="0"
                value={callMinutes}
                onChange={(e) => setCallMinutes(e.target.value)}
                placeholder="500"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="plan-desc">Beschreibung</Label>
            <Input
              id="plan-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kurzer Marketing-Text"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Features</Label>
            <div className="flex gap-2">
              <Input
                value={featureDraft}
                onChange={(e) => setFeatureDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFeature();
                  }
                }}
                placeholder="z.B. Unlimited Anrufe — Enter zum Hinzufügen"
              />
              <Button type="button" variant="outline" onClick={addFeature}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {features.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 pt-1">
                {features.map((f, i) => (
                  <li
                    key={i}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs"
                  >
                    <span>{f}</span>
                    <button
                      type="button"
                      onClick={() => removeFeature(i)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Entfernen"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <fieldset className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Mollie-Abrechnung
            </legend>
            <div className="space-y-1.5">
              <Label>Abrechnungs-Modus</Label>
              <Select
                value={saasKind ?? '__none__'}
                onValueChange={(v) =>
                  setSaasKind(v === '__none__' ? null : (v as 'subscription' | 'payg'))
                }
                disabled={saasKindLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Kein Mollie-Plan (rein interner Tier)</SelectItem>
                  <SelectItem value="subscription">Abo (monatlich, mit Quota)</SelectItem>
                  <SelectItem value="payg">Pay-as-you-go (Wallet, pro Minute)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">
                {saasKindLocked
                  ? 'Modus ist für diese kanonische Plan-ID verdrahtet — Mollie-Checkout + Quotenprüfung lesen daraus.'
                  : 'Wenn der Plan über Mollie verkauft werden soll, wähle Abo oder PAYG. Alles andere ist ein rein interner Tier.'}
              </div>
            </div>
            {saasKind === 'subscription' && (
              <div className="space-y-1.5">
                <Label htmlFor="plan-overage">
                  Überlauf-Preis pro Minute (Cent netto, nach Quota)
                </Label>
                <Input
                  id="plan-overage"
                  type="number"
                  min="0"
                  value={overagePerMinute}
                  onChange={(e) => setOveragePerMinute(e.target.value)}
                />
              </div>
            )}
            {saasKind === 'payg' && (
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="plan-perminute">Pro Minute (Cent netto)</Label>
                  <Input
                    id="plan-perminute"
                    type="number"
                    min="0"
                    value={perMinute}
                    onChange={(e) => setPerMinute(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="plan-default-topup">Default-Aufladung (Cent netto)</Label>
                  <Input
                    id="plan-default-topup"
                    type="number"
                    min="100"
                    value={defaultTopUp}
                    onChange={(e) => setDefaultTopUp(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="plan-min-topup">Mindest-Aufladung (Cent netto)</Label>
                  <Input
                    id="plan-min-topup"
                    type="number"
                    min="50"
                    value={minTopUp}
                    onChange={(e) => setMinTopUp(e.target.value)}
                  />
                </div>
              </div>
            )}
          </fieldset>

          {/* ── Sichtbarkeit ─────────────────────────────────────────
              Steuert, ob der Plan in der öffentlichen "Plan wechseln"-
              Karte (Nutzung & Kosten) für andere User auftaucht. Wenn
              "privat", sehen ihn nur die unten gelisteten User-IDs +
              alle Admins. Vom `isActive`-Schalter darunter unabhängig:
              ein deaktivierter Plan wird IMMER ausgeblendet. */}
          <fieldset className="space-y-3 rounded-md border border-border p-3">
            <legend className="px-1 text-xs uppercase tracking-wider text-muted-foreground">
              Sichtbarkeit für Endkunden
            </legend>
            <div className="flex flex-wrap gap-2 text-xs">
              <button
                type="button"
                onClick={() => setVisibilityMode('public')}
                className={cn(
                  'rounded-md border px-3 py-1.5 transition-colors',
                  visibilityMode === 'public'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-secondary',
                )}
              >
                Öffentlich · für alle sichtbar
              </button>
              <button
                type="button"
                onClick={() => setVisibilityMode('private')}
                className={cn(
                  'rounded-md border px-3 py-1.5 transition-colors',
                  visibilityMode === 'private'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-secondary',
                )}
              >
                Privat · nur freigeschaltete UIDs
              </button>
            </div>
            {visibilityMode === 'private' && (
              <div className="space-y-1.5">
                <Label htmlFor="plan-allowed-uids" className="text-xs">
                  Freigeschaltete User-IDs
                </Label>
                <textarea
                  id="plan-allowed-uids"
                  rows={4}
                  value={allowedUidsText}
                  onChange={(e) => setAllowedUidsText(e.target.value)}
                  placeholder={'Eine UID pro Zeile, z.B.\nxcvbWWlVzhSkM3TDydDeeDw3HKv2'}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
                  spellCheck={false}
                />
                <p className="text-[11px] text-muted-foreground">
                  Eine UID pro Zeile (oder kommasepariert). Admins sehen den Plan ohnehin —
                  diese Liste betrifft nur normale Endkunden.
                </p>
              </div>
            )}
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="plan-sort">Sortierung</Label>
              <Input
                id="plan-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Aktiv (im Benutzer-Editor wählbar)
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trash2 className="h-4 w-4" /> Abbrechen
          </Button>
          <Button onClick={onSave} disabled={upsert.isPending || rename.isPending}>
            {upsert.isPending || rename.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function autoSlug(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}
